package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ActorKind
import dev.lingvo.shared.AuditEventDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.shared.customerLabel
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LCodeBlock
import dev.lingvo.site.ds.LCodeLanguage
import dev.lingvo.site.ds.LDownloadIcon
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPageState
import dev.lingvo.site.ds.LPagination
import dev.lingvo.site.ds.LScrollTextIcon
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.narrowestActiveLabel
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.util.csvRow
import dev.lingvo.site.util.downloadText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

/**
 * The plural noun `LPagination` splices into its own sentences — "No events on this page."
 *
 * Never a `formatCount` unit. That parameter is the **singular**, so `formatCount(n, AUDIT_UNIT)`
 * rendered "24 eventss" in the filter bar's result count and "1 events" for a single row. Counted
 * strings on this page go through [tPlural], which is the only thing here that can conjugate for a
 * language that is not English.
 */
private const val AUDIT_UNIT = "events"

private val AUDIT_ACTIONS = listOf(
    "WORKSPACE_RENAMED", "OWNERSHIP_TRANSFERRED", "INVITATION_REVOKED", "INVITATION_RESENT", "MEMBER_JOINED",
    "MEMBER_ROLE_CHANGED", "MEMBER_INVITED", "AGENT_CONNECTED", "AGENT_LIMITS_CHANGED",
    "AGENTS_PAUSED_ALL", "AGENT_ROTATED", "AGENT_REVOKED", "WEBHOOK_CREATED",
    "WEBHOOK_SECRET_ROTATED", "WEBHOOK_DELETED", "CREDITS_GRANTED",
)

private fun Map<String, String>.asJsonText(): String = buildString {
    append("{\n")
    entries.sortedBy { it.key }.forEachIndexed { index, (key, value) ->
        append("  \"").append(key.escapeJson()).append("\": \"").append(value.escapeJson()).append("\"")
        if (index < size - 1) append(",")
        append("\n")
    }
    append("}")
}

private fun String.escapeJson(): String = replace("\\", "\\\\").replace("\"", "\\\"").replace("\n", "\\n")

/**
 * The action filter's options, in the words the log itself uses.
 *
 * The *value* stays the server's constant, because `GET /audit?action=` matches on it; only the
 * label changes. `MEMBER_ROLE_CHANGED` in a dropdown asks the reader of a governance page to know
 * the schema behind it — on the one page they are most likely to have been sent to by somebody else
 * ("check the audit log") rather than to have arrived at by exploring.
 *
 * Spelled out in a `when` rather than derived from the constant, because a key assembled at runtime
 * — `t("app.settings.audit.$action", …)` — is invisible to the catalogue extractor and could
 * therefore never be translated.
 */
@Composable
private fun auditActionLabel(action: String): String = when (action) {
    "WORKSPACE_RENAMED" -> t("app.settings.audit.workspace_renamed", "Workspace renamed")
    "OWNERSHIP_TRANSFERRED" -> t("app.settings.audit.ownership_transferred", "Ownership transferred")
    "INVITATION_REVOKED" -> t("app.settings.audit.invitation_withdrawn", "Invitation withdrawn")
    "INVITATION_RESENT" -> t("app.settings.audit.invitation_resent", "Invitation resent")
    "MEMBER_JOINED" -> t("app.settings.audit.member_joined", "Member joined")
    "MEMBER_ROLE_CHANGED" -> t("app.settings.audit.role_changed", "Role changed")
    "MEMBER_INVITED" -> t("app.settings.audit.member_invited", "Member invited")
    "AGENT_CONNECTED" -> t("app.settings.audit.agent_connected", "Agent connected")
    "AGENT_LIMITS_CHANGED" -> t("app.settings.audit.agent_limits_changed", "Agent limits changed")
    "AGENTS_PAUSED_ALL" -> t("app.settings.audit.all_agents_paused", "All agents paused")
    "AGENT_ROTATED" -> t("app.settings.audit.agent_credential_rotated", "Agent credential rotated")
    "AGENT_REVOKED" -> t("app.settings.audit.agent_revoked", "Agent revoked")
    "WEBHOOK_CREATED" -> t("app.settings.audit.webhook_created", "Webhook created")
    "WEBHOOK_SECRET_ROTATED" -> t("app.settings.audit.webhook_secret_rotated", "Webhook secret rotated")
    "WEBHOOK_DELETED" -> t("app.settings.audit.webhook_deleted", "Webhook deleted")
    "CREDITS_GRANTED" -> t("app.settings.audit.credits_granted", "Credits granted")
    // A constant this list has grown past still reads as words rather than as a schema name.
    // `AuditRepo.sentenceFor` ends in the same fallback for the same reason: a log with a row
    // nobody can parse is a log people stop reading.
    else -> action.lowercase().replace('_', ' ').replaceFirstChar(Char::uppercase)
}

@Page("/app/settings/audit")
@Layout(".components.layouts.AppLayout")
@Composable
fun AuditPage() {
    val ctx = rememberPageContext()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug
    var action by remember { mutableStateOf(UrlState.read(ctx, "action").orEmpty()) }
    var actor by remember { mutableStateOf(UrlState.read(ctx, "actor").orEmpty()) }
    val initialAfter = remember { UrlState.read(ctx, "after") }
    var stack by remember { mutableStateOf(if (initialAfter == null) listOf<String?>(null) else listOf(null, initialAfter)) }
    var jumpToNewest by remember { mutableStateOf(initialAfter != null) }
    var refreshKey by remember { mutableStateOf(0) }
    val cursor = stack.lastOrNull()
    // `AuditRoutes` is owners and admins, so a translator's request could only ever come back 403.
    // The refusal below is what they read; this is what stops the request being made at all. The
    // filter bar above depends on this page, so the load cannot simply sit under the gate the way
    // `settings/Usage.kt` puts its own — the condition moves into the block instead.
    val canReadAudit = workspace == null || workspace.role.atLeast(Role.ADMIN)
    val state = loadAsync(slug, action, cursor, refreshKey, canReadAudit) {
        if (!canReadAudit) null else slug?.let { ApiClient.auditLog(it, action.takeIf(String::isNotBlank), cursor) }
    }

    // **Only `action` is a server-side filter.** `loadAsync` re-keys on `slug`, `action` and
    // `cursor`; `actor` is deliberately absent, so typing a name narrows the page already in hand
    // and never the log. The narrowed list is therefore computed once and read by all three of the
    // rows, the result count above them and the pagination count below — counting `page.events`
    // for either total is what put "24 events" over three rows and then paged past a colleague.
    val loadedPage = when (state) {
        is Async.Success -> state.value
        else -> null
    }
    val visibleEvents = loadedPage?.events.orEmpty()
        .filter { actor.isBlank() || it.actorLabel.contains(actor, true) }

    fun syncFilters(nextAction: String = action, nextActor: String = actor) {
        UrlState.replace(mapOf("action" to nextAction, "actor" to nextActor))
    }
    fun clearFilters() {
        action = ""
        actor = ""
        stack = listOf(null)
        jumpToNewest = false
        syncFilters("", "")
    }

    val filterFields = listOf(
        LFilterField(
            id = "audit-action-filter", label = t("app.settings.audit.action", "Action"),
            // The chip repeats the choice back — `LFilterBar` renders "${label}: ${activeValue}" —
            // so it has to be the same words the dropdown offered. Passing the raw constant left
            // the reader picking "Role changed" and being shown "Action: MEMBER_ROLE_CHANGED",
            // which is the schema name the label exists to keep off this page. The `action` state
            // and the URL parameter are untouched: only what is displayed changes.
            activeValue = action.takeIf(String::isNotBlank)?.let { auditActionLabel(it) },
            onClear = {
                action = ""
                stack = listOf(null)
                jumpToNewest = false
                syncFilters("", actor)
            },
        ) { modifier ->
            LSelect(
                value = action,
                options = listOf(LSelectOption("", t("app.settings.audit.all_actions", "All actions"))) +
                    AUDIT_ACTIONS.map { LSelectOption(it, auditActionLabel(it)) },
                onValueChange = {
                    action = it
                    stack = listOf(null)
                    jumpToNewest = false
                    syncFilters(it, actor)
                },
                modifier = modifier.width(260.px), label = t("app.settings.audit.action", "Action"),
            )
        },
        LFilterField(
            id = "audit-actor-filter", label = t("app.settings.audit.actor", "Actor"),
            activeValue = actor.takeIf(String::isNotBlank),
            onClear = {
                actor = ""
                stack = listOf(null)
                jumpToNewest = false
                syncFilters(action, "")
            },
        ) { modifier ->
            LSearchField(
                value = actor,
                onValueChange = {
                    actor = it
                    stack = listOf(null)
                    jumpToNewest = false
                    syncFilters(action, it)
                },
                modifier = modifier.width(260.px), label = t("app.settings.audit.actor", "Actor"), placeholder = t("app.settings.audit.search_actor", "Search actor"),
                shortcutHint = null,
            )
        },
    )

    ProjectSettingsSurface {
        Column(Modifier.fillMaxWidth().gap(Space.XL)) {
            PageHeader(
                t("app.settings.audit.audit_log", "Audit log"),
                tf(
                    "app.settings.audit.member_billing_and_provider_changes_in_workspace",
                    "Member, billing and provider changes in {workspace}. String edits live in each " +
                        "project's activity.",
                    "workspace" to (workspace?.name ?: t("app.settings.audit.this_workspace", "this workspace")),
                ),
                mark = { LScrollTextIcon() },
                actions = {
                    // **The real count, and the honest scope.** This is the page a compliance question
                    // ends up on, and there was no way off it except a screenshot. The button says how
                    // many rows it will write, because a "Download CSV" that silently means "the
                    // ninety rows currently on screen" is the kind of export somebody files.
                    if (canReadAudit && visibleEvents.isNotEmpty()) {
                        LButton(
                            tPlural(
                                "app.settings.audit.count.download_event",
                                visibleEvents.size,
                                "Download {count} event",
                                "Download {count} events",
                            ),
                            {
                                downloadText(
                                    auditCsv(visibleEvents),
                                    "lingvo-audit-${workspace?.slug.orEmpty()}.csv",
                                    "text/csv;charset=utf-8",
                                )
                            },
                            variant = LButtonVariant.SECONDARY,
                            leadingIcon = { LDownloadIcon() },
                        )
                    }
                },
            )
            if (canReadAudit && visibleEvents.isNotEmpty()) {
                LMuted(
                    t(
                        "app.settings.audit.the_download_covers_this_page_under",
                        "The download covers this page of the log, under the filters above — not the whole history.",
                    ),
                )
            }
            // The refusal itself — after a wait, with the default subject, is what a translator used to
            // get instead. The usage pages state exactly this refusal for exactly this reason.
            if (!canReadAudit) {
                LEmptyState(
                    t("app.settings.audit.only_owners_and_admins_can_see", "Only owners and admins can see the audit log"),
                    t("app.settings.audit.ask_an_owner_or_an_admin", "Ask an owner or an admin if you need to know who changed what."),
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.FORBIDDEN,
                ) {}
                return@Column
            }
            // Operator rows are always inline. The actor search narrows toward a name, but there is no
            // control that can hide a category of actor from this governance page.
            LFilterBar(
                fields = filterFields,
                onClearAll = ::clearFilters,
                resultSummary = loadedPage?.let {
                    tPlural("app.settings.audit.events_in_view", visibleEvents.size, "{count} event", "{count} events")
                },
            )
            LAsyncContent(
                state,
                rows = 6,
                loadingLabel = t("app.settings.audit.loading_the_audit_log", "Loading the audit log"),
                subject = t("app.settings.audit.the_audit_log", "the audit log"),
                onRetry = { refreshKey++ },
            ) { page ->
                // The rows, the notices under them and the pager share one wrapper, so the space
                // between them is set here — the page column's gap falls outside it.
                Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                    when {
                        // Not "choose a workspace": the picker it sent the reader to is empty, because
                        // null here means this account is in no workspace at all.
                        page == null -> LEmptyState(
                            t("app.settings.audit.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                            t("app.settings.audit.a_workspace_holds_the_changes_this", "A workspace holds the changes this log records. Create one, or ask " +
                                "whoever invited you to send the invitation again."),
                            Modifier.fillMaxWidth(),
                            LEmptyStateIcon.AUDIT,
                        ) {
                            LButton(
                                t("app.settings.audit.create_a_workspace", "Create a workspace"),
                                { ctx.router.tryRoutingTo("/app/workspaces") },
                            )
                        }
                        page.events.isEmpty() && action.isBlank() && actor.isBlank() -> LEmptyState(
                            t("app.settings.audit.nothing_recorded_yet", "Nothing recorded yet"), t("app.settings.audit.member_billing_and_provider_changes_appear", "Member, billing and provider changes appear here."), Modifier.fillMaxWidth(), LEmptyStateIcon.AUDIT,
                        ) {}
                        visibleEvents.isEmpty() -> LEmptyState(
                            tf(
                                "app.settings.audit.no_match_for_filters",
                                "No {subject} match those filters",
                                "subject" to (
                                    filterFields.narrowestActiveLabel()?.lowercase()
                                        ?: t("app.settings.audit.events_subject", "events")
                                ),
                            ),
                            t("app.settings.audit.clear_them_to_see_everything_recorded", "Clear them to see everything recorded."), Modifier.fillMaxWidth(), LEmptyStateIcon.SEARCH,
                        ) { LButton(t("app.settings.audit.clear_filters", "Clear filters"), ::clearFilters, variant = LButtonVariant.SECONDARY) }
                        else -> {
                            visibleEvents.forEach { event -> EventRow(event) }
                            // Keyed off what is on screen, not off the fetched page: the notice explains
                            // a support row the reader can see, and an actor filter that hides every one
                            // of them leaves it explaining nothing.
                            operatorNoticeFor(visibleEvents)?.let { LMuted(it) }
                            LMuted(
                                tPlural(
                                    "app.settings.audit.retention_days",
                                    page.retentionDays,
                                    "Events are kept for {count} day on this plan, then deleted. " +
                                        "Nothing here can be edited or removed before then.",
                                    "Events are kept for {count} days on this plan, then deleted. " +
                                        "Nothing here can be edited or removed before then.",
                                ),
                            )
                        }
                    }
                    if (page != null) {
                        // Said only while the box is in use, because it is a fact about the filter and
                        // not about the log. Without it, somebody who searches for a colleague and gets
                        // three rows concludes that colleague did three things all year, when the rest
                        // are two pages back.
                        if (actor.isNotBlank()) {
                            LMuted(t("app.settings.audit.the_actor_filter_searches_only_the", "The actor filter searches only the events on this page. Somebody who acted earlier appears on an older page."))
                        }
                        LPagination(
                            state = LPageState(
                                shown = visibleEvents.size,
                                // A range — "Events 51 to 53" — is a claim about positions in the whole
                                // log, and the actor box filters after the fetch, so what is left is not
                                // a contiguous run of it. Dropping `pageSize` to zero makes
                                // `paginationSentence` fall back to a bare count, which is the only
                                // thing this page can still honestly say.
                                pageSize = page.events.size.takeIf { page.nextCursor != null && actor.isBlank() } ?: 0,
                                hasOlder = page.nextCursor != null,
                                hasNewer = stack.size > 1,
                                pageNumber = stack.size,
                                newerIsJumpToNewest = jumpToNewest,
                            ),
                            onOlder = {
                                page.nextCursor?.let {
                                    stack = stack + it
                                    jumpToNewest = false
                                    UrlState.push(mapOf("action" to action, "actor" to actor, "after" to it))
                                }
                            },
                            onNewer = {
                                if (stack.size > 1) {
                                    stack = stack.dropLast(1)
                                    jumpToNewest = false
                                    UrlState.push(mapOf("action" to action, "actor" to actor, "after" to stack.lastOrNull()))
                                }
                            },
                            modifier = Modifier.fillMaxWidth(), unit = AUDIT_UNIT,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun EventRow(event: AuditEventDto) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.XS)) {
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.Top) {
                LBadge(event.actorKind.customerLabel, variant = actorBadgeVariant(event.actorKind))
                P(Modifier.margin(0.px).flexGrow(1).toAttrs()) { Text(event.sentence) }
            }
            LMuted(
                tf(
                    "app.settings.audit.event_stamp_actor",
                    "{when} · {actor}",
                    "when" to formatUtc(event.createdAt, LTimeStyle.STAMP),
                    "actor" to event.actorLabel,
                ),
                Modifier.tabularNumbers(),
            )
            if (event.payload.isNotEmpty()) {
                LCodeBlock(event.payload.asJsonText(), label = t("app.settings.audit.detail", "Detail"), language = LCodeLanguage.JSON, maxLines = 8)
            }
        }
    }
}

internal fun actorBadgeVariant(kind: ActorKind): LBadgeVariant =
    if (kind == ActorKind.OPERATOR) LBadgeVariant.INFO else LBadgeVariant.NEUTRAL

internal const val OPERATOR_ROW_NOTICE: String =
    "A support row is something a person at Lingvo did to this workspace after being asked about it " +
        "— most often putting credits back after a billing mistake. Support has no way to read a " +
        "provider key, an API token or a password, and no way to sign in as anyone here."

internal fun operatorNoticeFor(events: List<AuditEventDto>): String? =
    OPERATOR_ROW_NOTICE.takeIf { events.any { event -> event.actorKind == ActorKind.OPERATOR } }

/**
 * The rows on screen, as a spreadsheet.
 *
 * Every field is quoted by [csvField] rather than only the ones that look risky today: an actor
 * label is a person's name, and a person's name gains a comma the day somebody writes "Smith, Jr".
 * The header row names the columns in the reader's language; the values are the server's own.
 */
internal fun auditCsv(events: List<AuditEventDto>): String = buildString {
    append(
        csvRow(
            listOf(
                tr("app.settings.audit.csv_when", "When"),
                tr("app.settings.audit.csv_actor", "Actor"),
                tr("app.settings.audit.csv_action", "Action"),
                tr("app.settings.audit.csv_target", "Target"),
                tr("app.settings.audit.csv_what_happened", "What happened"),
            ),
        ),
    )
    events.forEach { event ->
        append(
            csvRow(
                listOf(
                    event.createdAt,
                    event.actorLabel,
                    event.action,
                    event.targetLabel.orEmpty(),
                    event.sentence,
                ),
            ),
        )
    }
}
